import { useState, useEffect, useMemo } from "react";
import { readCsv, postcodesToLookUp, placesFromDistricts, planCsvLine, csvTotals } from "../../core/data/csvScans";
import { lookupPostcodes } from "../../core/api/postcodesApi";
import { startScan } from "../../core/api/workerApi";
import { tagScanBusiness } from "../../core/api/businessesApi";

// The CSV upload: the chosen file, its lines, the postcodes looked up, and
// each line planned as a scan (core/data/csvScans.js). Kept on the search
// page, so switching tabs doesn't lose the file.
export default function useCsvUpload({ districts, niches, maxPostcodes }) {
  // { name, lines, error }
  const [file, setFile] = useState(null);
  // { "S1 2AB": { postcode, lat, lng, places } }, or null while looking up.
  const [places, setPlaces] = useState(null);
  const [now, setNow] = useState(() => new Date());
  // After Run: { [lineNumber]: { status: "starting" | "started" | "failed", jobId, error, tagError } }
  const [results, setResults] = useState({});
  // "idle", "running" or "done" (a file is only run once).
  const [runState, setRunState] = useState("idle");

  const loadFile = async (chosen) => {
    if (!chosen) return;
    let text = "";
    try {
      text = await chosen.text();
    } catch (e) {
      setFile({ name: chosen.name, lines: [], error: "Couldn't read that file." });
      return;
    }
    const { lines, error } = readCsv(text);
    setPlaces(null);
    setNow(new Date());
    setResults({});
    setRunState("idle");
    setFile({ name: chosen.name, lines, error });
  };

  const clear = () => { setFile(null); setPlaces(null); setResults({}); setRunState("idle"); };

  // Districts on the list come from it; full postcodes (and districts not on
  // it) from postcodes.io. Waits for the district list to load.
  const lines = file ? file.lines : null;
  const ready = districts.length > 0;
  useEffect(() => {
    if (!lines || lines.length === 0 || !ready) return;
    let cancelled = false;
    const { found, toFetch } = placesFromDistricts(postcodesToLookUp(lines), districts);
    lookupPostcodes(toFetch).then(fetched => { if (!cancelled) setPlaces({ ...found, ...fetched }); });
    return () => { cancelled = true; };
  }, [lines, ready, districts]);

  const planned = useMemo(() => {
    if (!lines || !places || niches.length === 0) return null;
    return lines.map(line => planCsvLine(line, { places, districts, niches, maxPostcodes, now }));
  }, [lines, places, districts, niches, maxPostcodes, now]);

  // Creates the ready lines' scans one at a time, with the same request as the
  // search page, then tags each business. Lines with problems are left out.
  // Scans aren't kicked off one by one: the Worker starts every due sample
  // within a minute, so a big upload doesn't hit Oxylabs all at once.
  const runAll = async () => {
    if (!planned || runState !== "idle") return;
    setRunState("running");
    const update = (lineNumber, fields) => setResults(r => ({ ...r, [lineNumber]: { ...r[lineNumber], ...fields } }));
    for (const p of planned.filter(x => x.ready)) {
      const n = p.line.lineNumber;
      update(n, { status: "starting" });
      const { jobId, error } = await startScan(p.request);
      if (error) { update(n, { status: "failed", error }); continue; }
      update(n, { status: "started", jobId });
      if (p.line.domain && p.line.tags.length > 0) {
        const tagged = await tagScanBusiness(jobId, p.line.tags);
        if (tagged.error) update(n, { tagError: tagged.error });
      }
    }
    setRunState("done");
  };

  const counts = Object.values(results).reduce((c, r) => ({ ...c, [r.status]: (c[r.status] || 0) + 1 }), {});

  return {
    file,
    loadFile,
    clear,
    runAll,
    runState,
    results,
    started: counts.started || 0,
    failed: counts.failed || 0,
    planned,
    totals: planned ? csvTotals(planned) : null,
    lookingUp: !!lines && lines.length > 0 && !planned,
  };
}
